// Every tool the Forecast MCP server offers an agent, as Nova Studio lists it. Read from the AI
// gateway, which serves Forecast-MCP's own catalogue, so the list matches the tools the server runs.
export const McpToolKind = {
	Read: 'READ',
	Preview: 'PREVIEW',
	Write: 'WRITE',
} as const;
export type McpToolKind = (typeof McpToolKind)[keyof typeof McpToolKind];

export const MCP_TOOL_KIND_ORDER: McpToolKind[] = [McpToolKind.Read, McpToolKind.Preview, McpToolKind.Write];

export interface NovaMcpTool {
	name: string;
	kind: McpToolKind;
	// The MCP server module the tool lives in.
	module: string;
	// One line on what the tool does.
	purpose: string;
	// Only the project planner may call it.
	plannerOnly: boolean;
	// The feature flag a session needs to call it, or null.
	featureFlag: string | null;
}

// The token a prompt uses to refer to a tool. A reference, not data, so it may go in any prompt.
export function mcpToolToken(tool: NovaMcpTool): string {
	return `{tool:${tool.name}}`;
}

const KIND_BY_CATALOGUE_KIND: Record<string, McpToolKind> = {
	read: McpToolKind.Read,
	preview: McpToolKind.Preview,
	write: McpToolKind.Write,
};

const TOOL_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;

// The Studio never speaks of milestones; a tool that also acts on them is described by what else it
// does ("phase/milestone dates" reads "phase dates").
export function withoutMilestones(text: string): string {
	return text
		.replace(/\s*\/\s*milestones?\b/gi, '')
		.replace(/\bmilestones?\s*\/\s*/gi, '')
		.replace(/\s{2,}/g, ' ')
		.trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

// The catalogue shape this reader understands. The server bumps it when a field changes meaning.
export const TOOL_CATALOGUE_SCHEMA_VERSION = 1;

// The tools in the gateway's catalogue response, in name order. An entry with no usable name, kind or
// module is left out rather than shown with a blank. A response in another shape throws, so the list
// reads as failed rather than as no tools, and no saved {tool:x} is called unknown.
export function mapToolCatalogue(response: unknown): NovaMcpTool[] {
	if (!isRecord(response) || response.schemaVersion !== TOOL_CATALOGUE_SCHEMA_VERSION || !Array.isArray(response.tools)) {
		throw new Error('the MCP tool catalogue is not in a shape this Studio reads');
	}
	return response.tools
		.flatMap((entry): NovaMcpTool[] => {
			if (!isRecord(entry)) {
				return [];
			}
			const {name, kind, module, summary, plannerOnly, featureFlag} = entry;
			const mappedKind = typeof kind === 'string' ? KIND_BY_CATALOGUE_KIND[kind] : undefined;
			if (typeof name !== 'string' || !TOOL_NAME_PATTERN.test(name) || !mappedKind) {
				return [];
			}
			if (typeof module !== 'string' || module.trim() === '') {
				return [];
			}
			return [
				{
					name,
					kind: mappedKind,
					module,
					purpose: withoutMilestones(typeof summary === 'string' ? summary : ''),
					plannerOnly: plannerOnly === true,
					featureFlag: typeof featureFlag === 'string' && featureFlag !== '' ? featureFlag : null,
				},
			];
		})
		.sort((first, second) => first.name.localeCompare(second.name));
}
